import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import api from '../services/api';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';

// Validation schema using Yup
const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]*$/, 'Only letters and spaces allowed'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
    .max(255, 'Email is too long'),
  state_id: Yup.string()
    .required('State is required'),
  phone: Yup.string()
    .matches(/^[0-9+\-\s()]*$/, 'Phone number is invalid')
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number is too long')
    .required('Phone number is required'),
  message: Yup.string()
    .required('Message is required')
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message cannot exceed 1000 characters')
    .matches(/^[a-zA-Z0-9\s.,!?'-]*$/, 'Invalid characters in message'),
  agree: Yup.boolean()
    .oneOf([true], 'You must agree to the terms and conditions'),
});

const MessageForm = ({ contactInfoSection = {}, states = [] }) => {
  // Initial form values
  const initialValues = {
    name: '',
    email: '',
    state_id: '',
    phone: '',
    message: '',
    agree: false,
  };

  // State to manage cooldown period
  const [isCooldown, setIsCooldown] = useState(false);

  // Get reCAPTCHA execute function
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Handle form submission with reCAPTCHA
  const handleSubmit = async (values, { setSubmitting, resetForm, setStatus }) => {
    const sanitizedValues = {
      name: DOMPurify.sanitize(values.name),
      email: values.email.trim(),
      state_id: values.state_id,
      phone: DOMPurify.sanitize(values.phone),
      message: DOMPurify.sanitize(values.message),
      agree: values.agree,
    };

    if (!executeRecaptcha) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'reCAPTCHA not loaded. Please try again.',
        timer: 3000,
        showConfirmButton: false,
      });
      setSubmitting(false);
      return;
    }

    Swal.fire({
      title: 'Submitting...',
      text: 'Please wait while we process your message.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const recaptchaToken = await executeRecaptcha('submit_form');
      const payload = {
        ...sanitizedValues,
        recaptchaToken,
      };

      const response = await api.post('/web-content/post-messages', payload);
      Swal.close();

      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: DOMPurify.sanitize(response.data.message || 'Message submitted successfully!'),
        timer: 3000,
        showConfirmButton: false,
      });

      setStatus({ success: response.data.message || 'Message submitted successfully!' });
      resetForm();

      setIsCooldown(true);
      setTimeout(() => {
        setIsCooldown(false);
      }, 5000);
    } catch (error) {
      Swal.close();

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'An error occurred while submitting the form';

      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: DOMPurify.sanitize(errorMessage),
        timer: 3000,
        showConfirmButton: false,
      });

      setStatus({ error: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const messageUsText = contactInfoSection.message_us_text || 'Message us';
  const messageUsDescription = contactInfoSection.message_us_description || 'Fill up the form below, our team will get back soon';

  return (
    <div className="message-form">
      <div className="message-heading">
        <h4 className="mb-0">{messageUsText}</h4>
        <p>{messageUsDescription}</p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        context={{ states }}
      >
        {({ isSubmitting, status }) => (
          <Form>
            <div className="form-group">
              <Field
                type="text"
                name="name"
                placeholder="Name"
                className="form-control"
                disabled={isSubmitting || isCooldown}
                maxLength={50}
                pattern="[A-Za-z\s]*"
                title="Only letters and spaces allowed"
              />
              <ErrorMessage name="name" component="div" className="text-danger" />
            </div>

            <div className="form-group">
              <Field
                type="email"
                name="email"
                placeholder="Email"
                className="form-control"
                disabled={isSubmitting || isCooldown}
                maxLength={255}
              />
              <ErrorMessage name="email" component="div" className="text-danger" />
            </div>

            <div className="form-group">
              <Field
                as="select"
                name="state_id"
                className="form-select"
                disabled={isSubmitting || isCooldown}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_id}>
                    {state.full_name}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="state_id" component="div" className="text-danger" />
            </div>

            <div className="form-group">
              <Field
                type="text"
                name="phone"
                placeholder="Phone"
                className="form-control"
                disabled={isSubmitting || isCooldown}
                maxLength={20}
                pattern="[0-9+\-\s()]*"
                title="Only numbers, +, -, spaces, and parentheses allowed"
              />
              <ErrorMessage name="phone" component="div" className="text-danger" />
            </div>

            <div className="form-group text-group">
              <Field
                as="textarea"
                name="message"
                className="form-control"
                placeholder="Your message"
                disabled={isSubmitting || isCooldown}
                maxLength={1000}
              />
              <ErrorMessage name="message" component="div" className="text-danger" />
            </div>

            <div className="form-group agree-text">
              <Field
                type="checkbox"
                name="agree"
                id="agree"
                disabled={isSubmitting || isCooldown}
              />
              <label htmlFor="agree">I agree to terms and conditions.</label>
              <ErrorMessage name="agree" component="div" className="text-danger" />
            </div>

            <div className="form-group mb-0 text-center">
              <button
                type="submit"
                className="contact-btn submit-btn"
                disabled={isSubmitting || isCooldown}
              >
                {isSubmitting
                  ? 'Submitting...'
                  : isCooldown
                  ? 'Please wait...'
                  : 'Submit your Message'}
              </button>
            </div>

            {status && status.success && (
              <div className="text-success text-center mt-2">{status.success}</div>
            )}
            {status && status.error && (
              <div className="text-danger text-center mt-2">{status.error}</div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MessageForm;